import type { MockMessage } from "@/dummy";

/**
 * A rendered row in the thread — either a real message bubble, a date
 * separator chip ("Today" / "Yesterday" / "17 Apr"), or the single
 * "unread messages" divider. `buildMessageRows` interleaves the separators
 * and divider into the chronological message list so the renderer can map
 * straight over the result.
 */
export type MessageRow =
  | { kind: "date"; key: string; label: string }
  | { kind: "unread"; key: string }
  | { kind: "message"; key: string; message: MockMessage };

export interface BuildMessageRowsOptions {
  locale: string;
  /** Localised "Today" / "Yesterday" labels for the relative separators. */
  todayLabel: string;
  yesterdayLabel: string;
  /** Id of the FIRST unread message — an "unread" divider row is inserted
   *  directly before it. `null` / absent → no divider. */
  unreadAnchorId?: string | null;
  /** Clock used for the today/yesterday comparison. Injectable for tests;
   *  defaults to `new Date()`. */
  now?: Date;
}

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** A message's timestamp, defaulting a missing `createdAt` to `now` — an
 *  optimistic/live append with no timestamp is "just now", so it groups
 *  under today. */
function timestampOf(m: MockMessage, now: Date): Date {
  if (m.createdAt) {
    const d = new Date(m.createdAt);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return now;
}

/** Date-separator label: Today / Yesterday / "17 Apr" (same year) /
 *  "17 Apr 2024" (older). */
function dateLabel(d: Date, o: BuildMessageRowsOptions, now: Date): string {
  if (sameDay(d, now)) return o.todayLabel;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return o.yesterdayLabel;
  return d.toLocaleDateString(
    o.locale,
    d.getFullYear() === now.getFullYear()
      ? { day: "numeric", month: "short" }
      : { day: "numeric", month: "short", year: "numeric" },
  );
}

/**
 * A message with nothing to show: no text, media, quick replies, or lifecycle notice. It would
 * render as an empty bubble (e.g. a reply built from a translator whose bundle had not landed),
 * so no row is emitted for it.
 */
export function isBlankMessage(m: MockMessage): boolean {
  return (
    !m.system &&
    !(m.text ?? "").trim() &&
    !(m.media && m.media.length > 0) &&
    !(m.buttons && m.buttons.length > 0)
  );
}

/**
 * Interleave date separators + the unread divider into a chronological
 * message list. A date row is emitted whenever the calendar day changes
 * from the previous message; the unread divider is emitted once, directly
 * before the message whose id matches `unreadAnchorId`.
 */
export function buildMessageRows(
  messages: MockMessage[],
  o: BuildMessageRowsOptions,
): MessageRow[] {
  const now = o.now ?? new Date();
  const rows: MessageRow[] = [];
  let lastDayKey: string | null = null;

  for (const m of messages) {
    if (isBlankMessage(m)) continue;
    const ts = timestampOf(m, now);
    const dayKey = `${ts.getFullYear()}-${ts.getMonth()}-${ts.getDate()}`;
    if (dayKey !== lastDayKey) {
      lastDayKey = dayKey;
      rows.push({ kind: "date", key: `date-${dayKey}`, label: dateLabel(ts, o, now) });
    }
    // The unread divider sits ABOVE its anchor message but BELOW that
    // message's date separator (emitted just above).
    if (o.unreadAnchorId && m.id === o.unreadAnchorId) {
      rows.push({ kind: "unread", key: "unread-divider" });
    }
    // Prefer `clientKey` when present — it stays stable across the
    // tmp-id → BE-id swap that happens when an optimistic send
    // resolves, so React's `<AnimatePresence>` doesn't see the row
    // as a fresh element and re-play the entrance animation. History
    // messages don't carry one; `id` is the BE-canonical stable id
    // for those.
    rows.push({ kind: "message", key: m.clientKey ?? m.id, message: m });
  }

  return rows;
}
